import { z } from 'zod';
import type { Widget } from './project.widgets.js';

export enum SegmentationDashboardWidget {
  ChartVisualization = 'chart-visualization',
  Media = 'media',
  Quote = 'quote',
  RichText = 'rich-text',
  TagSummary = 'tag-summary',
}

export interface WidgetPropsByType {
  [SegmentationDashboardWidget.ChartVisualization]: Widget.ChartVisualizationProps;
  [SegmentationDashboardWidget.Media]: Widget.MediaProps;
  [SegmentationDashboardWidget.Quote]: Widget.QuoteProps;
  [SegmentationDashboardWidget.RichText]: Widget.RichTextProps;
  [SegmentationDashboardWidget.TagSummary]: Widget.TagSummaryProps;
}

export interface BaseWidgetInstance<T extends SegmentationDashboardWidget> {
  id: number;
  type: T;
  title: string;
  props?: WidgetPropsByType[T];
  sourceMetadata?: WidgetSourceMetadata;
}

export type WidgetItem =
  | Widget.ChartVisualization
  | Widget.Media
  | Widget.Quote
  | Widget.RichText
  | Widget.TagSummary;

export type WidgetChart = BaseWidgetInstance<SegmentationDashboardWidget.ChartVisualization>;

export type WidgetInstance = {
  [T in SegmentationDashboardWidget]: BaseWidgetInstance<T>;
}[SegmentationDashboardWidget];

// Inline content types
export const RichTextContentSchema = z.object({
  type: z.literal('rich-text'),
  data: z.object({
    content: z.record(z.string(), z.unknown()).optional(),
    title: z.string().optional(),
  }),
});
export type RichTextContent = z.infer<typeof RichTextContentSchema>;

const InlineContentTypes = z.discriminatedUnion('type', [RichTextContentSchema]);

export const InlineContentSchema = z
  .object({
    id: z.string(),
  })
  .and(InlineContentTypes);
export type InlineContent = z.infer<typeof InlineContentSchema>;

// Union type for section content items
export const SectionContentItemSchema = z.union([
  z.object({
    type: z.literal('widget'),
    widgetId: z.number(),
    width: z.number().optional(),
  }),
  z.object({
    type: z.literal('inline'),
    content: InlineContentSchema,
    width: z.number().optional(),
  }),
]);
export type SectionContentItem = z.infer<typeof SectionContentItemSchema>;

export const SectionLayoutSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  tag: z
    .object({
      tagId: z.number(),
      tagName: z.string(),
    })
    .optional(),
  contentItems: z.array(SectionContentItemSchema),
});
export type SectionLayout = z.infer<typeof SectionLayoutSchema>;

export const BlockLayoutSchema = z.object({
  id: z.string(),
  title: z.string().optional(),

  tag: z
    .object({
      tagId: z.number(),
      tagName: z.string(),
    })
    .optional(),
  sections: z.array(z.lazy(() => SectionLayoutSchema)).optional(),
  widgetId: z.number().optional(),
  inlineContent: InlineContentSchema.optional(),
});
export type BlockLayout = z.infer<typeof BlockLayoutSchema>;

export const WidgetGridLayoutSchema = z.object({
  blocks: z.array(BlockLayoutSchema),
});
export type WidgetGridLayout = z.infer<typeof WidgetGridLayoutSchema>;

// Widget Source Metadata Types
export enum WidgetSourceType {
  QuantAnalysis = 'quant-analysis',
  BrandInsights = 'brand-insights',
  Manual = 'manual',
  Other = 'other',
}

const stringListOrMatrixSchema = z
  .union([z.array(z.string()), z.array(z.array(z.string()))])
  .transform((value): string[][] => {
    if (value.length === 0) return [];
    return Array.isArray(value[0]) ? (value as string[][]) : [value as string[]];
  });

export const QuantAnalysisFilterInfoSchema = z.object({
  // Some endpoints return `string[]` here; normalize to `string[][]`.
  columnNames: stringListOrMatrixSchema.optional(),
  rowNames: stringListOrMatrixSchema.optional(),
});
export type QuantAnalysisFilterInfo = z.infer<typeof QuantAnalysisFilterInfoSchema>;

export const QuantAnalysisSourceInfoSchema = z.object({
  projectId: z.number(),
  projectName: z.string(),
  quantAnalysisId: z.number(),
  quantAnalysisName: z.string(),
  // Backend includes this; keep optional for compatibility.
  versionNumber: z.number().optional(),
  itemId: z.number(),
  itemName: z.string(),
  filters: QuantAnalysisFilterInfoSchema.nullable().optional(),
});
export type QuantAnalysisSourceInfo = z.infer<typeof QuantAnalysisSourceInfoSchema>;

export const QuantAnalysisWidgetSourceMetadataSchema = z.object({
  type: z.literal(WidgetSourceType.QuantAnalysis),
  createdAt: z.string(),
  quantAnalysis: QuantAnalysisSourceInfoSchema,
});
export type QuantAnalysisWidgetSourceMetadata = z.infer<typeof QuantAnalysisWidgetSourceMetadataSchema>;

export const BrandInsightsWidgetSourceMetadataSchema = z.object({
  type: z.literal(WidgetSourceType.BrandInsights),
  createdAt: z.string(),
});
export type BrandInsightsWidgetSourceMetadata = z.infer<typeof BrandInsightsWidgetSourceMetadataSchema>;

export const ManualWidgetSourceMetadataSchema = z.object({
  type: z.literal(WidgetSourceType.Manual),
  createdAt: z.string(),
});
export type ManualWidgetSourceMetadata = z.infer<typeof ManualWidgetSourceMetadataSchema>;

export const OtherWidgetSourceMetadataSchema = z.object({
  type: z.literal(WidgetSourceType.Other),
  createdAt: z.string(),
});
export type OtherWidgetSourceMetadata = z.infer<typeof OtherWidgetSourceMetadataSchema>;

export const WidgetSourceMetadataSchema = z.discriminatedUnion('type', [
  QuantAnalysisWidgetSourceMetadataSchema,
  BrandInsightsWidgetSourceMetadataSchema,
  ManualWidgetSourceMetadataSchema,
  OtherWidgetSourceMetadataSchema,
]);
export type WidgetSourceMetadata = z.infer<typeof WidgetSourceMetadataSchema>;

export type DataTableSortConfig = {
  columnIndex: number;
  direction: 'asc' | 'desc';
};

// QuickChartData is now derived from ChartWidgetDataSchema (dashboard-data.ts) —
// that Zod schema is the single source of truth for both the TypeScript type
// and runtime validation. Re-exporting here preserves the public API surface.
export type { ChartWidgetData as QuickChartData } from './dashboard-data.js';
